import React, { useState } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import FormContainer from '../../../FormContainer';
import Loans from './Loans';
import ActionsMenu from '../../../../ActionsMenu';

import useLoanGroupForm from '../../../../../forms/lender/loanGroup';
import LoanGroupService from '../../../../../services/LoanGroupService';
import { useUiContext } from '../../../../../context/UiContext';
import downloadDocument from '../../../../../helpers/downloadDocument';

export default function LoanGroup({ lenderId }) {
  const [isDownloadingTape, setIsDownloadingTape] = useState(false);

  const navigate = useNavigate();
  const { resourceId: id } = useParams();

  const { showSuccessToast, showApiErrorToast } = useUiContext();

  const isEdit = !!id && id !== 'new';

  const { data: loanGroup, refetch } = useQuery({
    queryKey: ['loanGroup', id],
    queryFn: () => LoanGroupService.getLoanGroup(id),
    enabled: !!id && id !== 'new',
  });

  const { mutate: createLoanGroup, isPending: isCreating } = useMutation({
    mutationFn: (formValues) =>
      LoanGroupService.createLoanGroup({
        ...formValues,
        lender_id: lenderId,
        loan_id: [],
      }),
    onSuccess: (newLoanGroup) => {
      showSuccessToast('Loan group created successfully');
      navigate(
        `/lenders/${lenderId}/loan-groups/${newLoanGroup.id}?tab=Loan Group`
      );
    },
    onError: (error) => {
      showApiErrorToast(error);
    },
  });

  const { mutate: updateLoanGroup, isPending: isUpdating } = useMutation({
    mutationFn: (formValues) => {
      try {
        const formatLoanIds = (loanIds) => {
          if (!Array.isArray(loanIds) || loanIds.length === 0) {
            return [];
          }

          const isObject =
            loanIds[0] && typeof loanIds[0] === 'object' && loanIds[0] !== null;

          if (isObject) {
            return loanIds?.filter?.(Boolean)?.map?.((loan) => loan?._loan?.id);
          }
          return loanIds;
        };

        return LoanGroupService.updateLoanGroup({
          loanGroupId: id,
          formValues: {
            ...formValues,
            loan_id: formatLoanIds(formValues.loan_id),
          },
        });
      } catch (error) {
        console.log('Error in updateLoanGroup:', error);
        throw error;
      }
    },
    onSuccess: () => {
      showSuccessToast('Loan Group updated successfully');
      refetch();
    },
    onError: (error) => {
      showApiErrorToast(error);
    },
  });

  const form = useLoanGroupForm(
    isEdit ? updateLoanGroup : createLoanGroup,
    loanGroup
  );

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <FormContainer
        isSubmitting={isCreating || isUpdating}
        form={form}
        noBillComId
        groupSizeOffset={2222}
        headerRight={
          <ActionsMenu
            isLoading={isDownloadingTape}
            actions={[
              {
                actionTitle: 'Download tape',
                actionDescription:
                  'A CSV loan tape will be created as of the date of the loan group.',
                onClick: async () => {
                  try {
                    setIsDownloadingTape(true);
                    const tape = await LoanGroupService.getLoanGroupTapes(id);

                    downloadDocument(
                      tape.id,
                      tape?.private_document?.name || 'tape.csv'
                    );
                  } catch (error) {
                    showApiErrorToast(error);
                  } finally {
                    setIsDownloadingTape(false);
                  }
                },
              },
            ]}
          />
        }
      />
      {isEdit && (
        <div className="mt-10">
          <Loans updateLoanGroup={updateLoanGroup} loanGroup={loanGroup} />
        </div>
      )}
    </div>
  );
}
